import { CONTACT_EMAIL, type IdentityPreference } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Ltr } from "../components/Bdi.js";
import { useToast } from "../components/Toast.js";
import { useT } from "../i18n/LocaleProvider.js";
import { api, ApiError } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";

interface Lookup {
  kind: string;
  status: string;
  used: boolean;
  identityPreference: IdentityPreference;
  displayName: string | null;
  createdAt: string;
}

/** The private capability link handed out once at submission: read, re-credit, or erase. */
export function WithdrawPage() {
  const { m } = useT();
  useDocumentTitle(m.meta.pages.withdraw);
  const { token = "" } = useParams();
  const toast = useToast();
  const [info, setInfo] = useState<Lookup | null | "invalid">(null);
  const [identity, setIdentity] = useState<IdentityPreference>("anonymous");
  const [name, setName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .post<Lookup>("/api/withdraw/lookup", { token })
      .then((r) => {
        setInfo(r);
        setIdentity(r.identityPreference);
        setName(r.displayName ?? "");
      })
      .catch(() => setInfo("invalid"));
  }, [token]);

  async function saveIdentity() {
    setBusy(true);
    try {
      await api.post("/api/withdraw", { token, action: "update_identity", identityPreference: identity, displayName: identity === "anonymous" ? undefined : name.trim() });
      toast(m.withdraw.saved);
    } catch {
      toast(m.common.error);
    } finally {
      setBusy(false);
    }
  }

  async function withdraw() {
    setBusy(true);
    try {
      await api.post("/api/withdraw", { token, action: "withdraw" });
      setWithdrawn(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setInfo((i) => (i && i !== "invalid" ? { ...i, used: true } : i));
      else toast(m.common.error);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (info === null) {
    return (
      <div className="container container--narrow section">
        <p className="muted">{m.common.loading}…</p>
      </div>
    );
  }
  if (info === "invalid" || withdrawn) {
    return (
      <div className="container container--narrow section">
        <h1>{m.withdraw.heading}</h1>
        <p className="notice" role="status">
          {withdrawn ? m.withdraw.withdrawn : m.withdraw.invalid}
        </p>
      </div>
    );
  }

  return (
    <div className="container container--narrow">
      <div className="page-head">
        <h1>{m.withdraw.heading}</h1>
        <p className="page-head__lede">{m.withdraw.body}</p>
      </div>
      <div className="form">
        <div className="card">
          <dl className="kv">
            <dt>{m.withdraw.statusLabel}</dt>
            <dd>
              <span className="badge">{m.admin.statuses[info.status as keyof typeof m.admin.statuses] ?? info.status}</span>
            </dd>
            <dt>{m.share.formats[info.kind as keyof typeof m.share.formats]?.title ?? info.kind}</dt>
            <dd className="muted">{new Date(info.createdAt).toLocaleDateString(m === undefined ? "en" : undefined)}</dd>
          </dl>
        </div>

        <form
          className="card form"
          onSubmit={(e) => {
            e.preventDefault();
            void saveIdentity();
          }}
        >
          <h2 style={{ fontSize: "1.2rem", margin: 0 }}>{m.withdraw.identityHeading}</h2>
          <div className="radio-inline">
            {(["real_name", "pseudonym", "anonymous"] as IdentityPreference[]).map((v) => (
              <label key={v}>
                <input type="radio" name="identity" value={v} checked={identity === v} onChange={() => setIdentity(v)} />
                {m.share.identity[v]}
              </label>
            ))}
          </div>
          {identity !== "anonymous" && (
            <input className="input" dir="auto" aria-label={identity === "pseudonym" ? m.share.pseudonymLabel : m.share.nameLabel} value={name} onChange={(e) => setName(e.target.value)} required />
          )}
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {m.withdraw.save}
            </button>
          </div>
        </form>

        <div className="card">
          <h2 style={{ fontSize: "1.2rem" }}>{m.withdraw.withdrawHeading}</h2>
          {info.used ? (
            <p className="notice">
              {m.withdraw.usedNotice.split(CONTACT_EMAIL)[0]}
              <Ltr>{CONTACT_EMAIL}</Ltr>
              {m.withdraw.usedNotice.split(CONTACT_EMAIL)[1]}
            </p>
          ) : (
            <>
              <p className="muted">{m.withdraw.withdrawBody}</p>
              {confirming ? (
                <div className="actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setConfirming(false)} disabled={busy}>
                    {m.withdraw.cancel}
                  </button>
                  <button type="button" className="btn btn--danger" onClick={() => void withdraw()} disabled={busy}>
                    {m.withdraw.confirm}
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn--danger" onClick={() => setConfirming(true)}>
                  {m.withdraw.withdrawButton}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
